import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";

// Add-on toggle for a PDP. Renders nothing when the add-on is disabled
// or out of stock. Reports selection state via `onChange(addon | null)`
// so the parent PDP can pass the modifier into `addItem(product, qty,
// variant, addons)`, the CartContext then stores it on the same line
// as the base product (no separate cart line).
//
// Scoped ONLY to the PDPs that ask for it (Raw Vial gets Recon Kit;
// Pen Kit gets Travel Case + Sharps Bin). Not a general-purpose card.
export default function AddonToggle({ addonKey, blurb, onChange, testIdPrefix }) {
  const [selected, setSelected] = useState(false);
  const { format: fmtPrice } = useCurrency();

  const { data: addons } = useQuery({
    queryKey: ["site-settings", "addons"],
    queryFn: async () => (await api.get(`/site-settings/addons`)).data,
    staleTime: 60 * 1000,
  });
  const config = addons?.[addonKey];

  // Hide entirely when the admin has disabled the add-on, no stock, or
  // config hasn't loaded yet, the buyer shouldn't see a phantom choice.
  if (!config || !config.enabled || config.stock <= 0) return null;

  const toggle = (nextChecked) => {
    setSelected(nextChecked);
    if (!onChange) return;
    onChange(
      nextChecked
        ? { sku: config.sku, name: config.name, price: config.price }
        : null
    );
  };

  return (
    <label
      className={`mt-4 flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${
        selected
          ? "border-brandblue bg-brandbluelight"
          : "border-brandblue/30 bg-white hover:border-brandblue"
      }`}
      data-testid={`${testIdPrefix}-wrap`}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={(e) => toggle(e.target.checked)}
        className="mt-1 h-4 w-4 shrink-0 accent-brandblue"
        data-testid={`${testIdPrefix}-checkbox`}
      />
      <span className="flex-1">
        <span className="flex items-baseline justify-between gap-3">
          <span className="font-heading text-sm font-bold text-ink">
            Add {config.name}
          </span>
          <span className="font-mono text-[12px] font-semibold text-brandbluedark" data-testid={`${testIdPrefix}-price`}>
            + {fmtPrice(config.price)}
          </span>
        </span>
        {blurb && (
          <span className="mt-1 block text-xs text-subtle">{blurb}</span>
        )}
        <span
          className={`mt-1 inline-block font-mono text-[10px] tracking-[0.14em] ${
            selected ? "text-brandblue" : "text-subtle"
          }`}
          data-testid={`${testIdPrefix}-status`}
        >
          {selected ? "● WILL BE ADDED TO CART" : "○ NOT ADDED"}
        </span>
      </span>
    </label>
  );
}
